import { useOnChange } from "@prismicio/editor-support/React"
import { Box, type IconName, Toast } from "@prismicio/editor-ui"
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useState
} from "react"

import { useMediaLibrary } from "./MediaLibraryContext"

const MediaLibraryToastContext = createContext<MediaLibraryToastContextValue>({
  showToast: () => {}
})

interface MediaLibraryToastContextValue {
  showToast: (config: ToastConfig) => void
}

interface MediaLibraryToastProviderProps {
  children: ReactNode
}

interface ToastConfig {
  title: string
  seconds?: number
  action?: {
    title: string
    onClick: () => void
  }
  icon?: IconName
}

export function MediaLibraryToastProvider(
  props: MediaLibraryToastProviderProps
) {
  const { children } = props
  const { isFetchingMedia } = useMediaLibrary()

  const [toast, setToast] = useState<ToastConfig>({
    title: "",
    seconds: 5
  })
  const [open, setOpen] = useState(false)

  const { title, action, seconds = 1, icon = "check" } = toast

  const value = useMemo(
    () => ({
      showToast: (config: ToastConfig) => {
        setToast({ seconds, ...config })
        setOpen(true)
      }
    }),
    [seconds]
  )

  return (
    <>
      <FetchingMediaToast open={isFetchingMedia} />
      <CacheInvalidatedToast />
      <Toast
        anchor={
          <Box
            position="absolute"
            justifyContent="center"
            width="100%"
            bottom={72}
          />
        }
        open={open}
        onOpenChange={setOpen}
        title={title}
        action={action}
        seconds={seconds}
        icon={icon}
      />
      <MediaLibraryToastContext.Provider value={value}>
        {children}
      </MediaLibraryToastContext.Provider>
    </>
  )
}

export function useMediaLibraryToast() {
  const context = useContext(MediaLibraryToastContext)

  return context
}

interface FetchingMediaToastProps {
  open: boolean
}

function FetchingMediaToast(props: FetchingMediaToastProps) {
  const { open } = props

  return (
    <Toast
      anchor={
        <Box
          position="absolute"
          justifyContent="center"
          width="100%"
          bottom={72}
        />
      }
      loadingIndicator
      open={open}
      title="Loading more items ..."
      seconds={1}
    />
  )
}

function CacheInvalidatedToast() {
  const { revalidateMedia, isMediaCacheInvalid } = useMediaLibrary()
  const [open, setOpen] = useState(isMediaCacheInvalid)

  useOnChange(isMediaCacheInvalid, setOpen)

  return (
    <Toast
      anchor={
        <Box
          position="absolute"
          justifyContent="center"
          width="100%"
          bottom={72}
        />
      }
      open={open}
      title="There has been changes to your media library"
      action={{
        title: "Update",
        onClick: revalidateMedia
      }}
      cancel={{
        onClick: () => setOpen(false)
      }}
    />
  )
}
